// #12993 — AI Insights monthly allowance.
//
// The finite limit keys an operator may set on the tenant's `insights`
// entitlement `limits` (the audited admin override), their defaults, and the
// AllowanceV1 wire shape. Shared by modules/tenants (apply-route validation),
// modules/insights (enforcement), the api-client, and apps/admin.
//
// There is deliberately NO representation of "unlimited": issue policy is
// "never grant unlimited usage", so every override is an integer 1..10000 and
// an absent key means the default.
import { z } from '../zod-openapi-init.js';
export const INSIGHTS_LIMIT_KEYS = {
    question: 'insights_monthly_questions',
    summary: 'insights_monthly_summaries',
};
/** Provisional defaults (owner sign-off pending in the #12993 cost memo). */
export const INSIGHTS_ALLOWANCE_DEFAULTS = {
    question: 5,
    summary: 100,
};
export const INSIGHTS_LIMIT_MIN = 1;
export const INSIGHTS_LIMIT_MAX = 10_000;
export function isValidInsightsLimitValue(v) {
    return (typeof v === 'number' &&
        Number.isInteger(v) &&
        v >= INSIGHTS_LIMIT_MIN &&
        v <= INSIGHTS_LIMIT_MAX);
}
const KNOWN_KEYS = new Set(Object.values(INSIGHTS_LIMIT_KEYS));
/** Problems with a proposed `limits` object for module `insights`; [] means valid. */
export function validateInsightsLimits(limits) {
    const problems = [];
    for (const [key, value] of Object.entries(limits)) {
        if (!KNOWN_KEYS.has(key)) {
            problems.push(`unknown insights limit key: ${key}`);
        }
        else if (!isValidInsightsLimitValue(value)) {
            problems.push(`${key} must be an integer from ${INSIGHTS_LIMIT_MIN} to ${INSIGHTS_LIMIT_MAX}`);
        }
    }
    return problems;
}
/**
 * One bucket's usage for the current UTC calendar month. `resetsOn` is the
 * first day of the next UTC month -- a date-only value (render it with a
 * calendar-date formatter, never a timezone conversion).
 */
export const AllowanceV1Schema = z.object({
    bucket: z.enum(['question', 'summary']),
    used: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
    remaining: z.number().int().nonnegative(),
    resetsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/** GET /api/v1/insights/usage `data` payload. */
export const InsightsUsageV1Schema = z.object({
    question: AllowanceV1Schema,
    summary: AllowanceV1Schema,
});
//# sourceMappingURL=ai-allowance.js.map